import { ContractState, ContractViewModel, QuoteReason } from '@ail/contracts-api'
import { CustomerIndexViewModel } from '@ail/customers-api'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RouteComponentProps } from 'react-router-dom'
import { Arr } from '../../helpers/Arr'
import { useSettings } from '../../hooks/useSettings'
import { formatCurrencyToSwissLocale, formatNumberToLocale, isDateValid, printDate } from '../../helpers/Utils'
import { useFilteredCustomers } from '../../hooks/useFilteredCustomers'
import { useGetContractsBySecurityKeysQuery } from '../../redux/api/contractsApi'
import { useGetAllUnitsQuery, useGetTenantsQuery } from '../../redux/api/customerApi'
import { useGetOrdersFormsQuery } from '../../redux/api/ordersApi'
import { useAppSelector } from '../../redux/store'
import { productTypeIdMap } from '../../types/constants'
import { DriverFilter } from '../../types/enums'
import { Contracts } from '../../utils/contracts'
import { ContractCostCenterCell } from '../DeliveryAndReturns/GridCells/CostCenterCell/CostCenterCell'
import ObjTypeCell from '../DeliveryAndReturns/GridCells/ObjectTypeCell/ObjectTypeCell'
import { ColKeysEnum } from '../GeneralContracts/GeneralContractsEnums'
import Alert from '../common/Alert/Alert'
import {
  Grid,
  GridColumn,
  InlineTextFilter,
  ModalContractsDropdownFilter,
  ModalDriverFilter,
  ModalReplacementStateFilter,
} from '../common/Grid/Grid'
import LicencePlateFlag from '../common/LicencePlateFlag/LicencePlateFlag'
import './GeneralContractsFunctional.scss'
import ContractCell from './parts/ContractCell'
import { useReplacementCondition } from '../../hooks/useReplacementCondition'
import ReplacementStateCell from './ReplacementState/ReplacementStateCell'
import { SelectionState } from './ReplacementState/ReplacementStateSelector'
import { useIsInterfleetPro } from '../../hooks/useIsInterfleetPro'
import { Loader } from '../common/Loader/Loader'

// #region TYPES
export interface GridRow {
  contract: ContractViewModel
  customer?: CustomerIndexViewModel
  replacedContract?: ContractViewModel
  futureContract?: ContractViewModel
  type: string
  costCenter: string
  administration?: string
  unit?: string
  state: ContractState | 'Expiring' | 'SettingUp'
  manager?: string
  replacementOrderCondition?: {
    missingReplacementOrder: boolean
    returnWithoutReplacement: boolean
    processingOrder: boolean
  }
}

type GeneralContractsFilters = {
  orderStatus: ModalContractsDropdownFilter<GridRow>['filterFn']
  contract: InlineTextFilter<GridRow>['filterFn']
  costCenter: ModalContractsDropdownFilter<GridRow>['filterFn']
  replacedContractsRefNumber: InlineTextFilter<GridRow>['filterFn']
  makes: ModalContractsDropdownFilter<GridRow>['filterFn']
  model: InlineTextFilter<GridRow>['filterFn']
  driver: ModalDriverFilter<GridRow>['filterFn']
  gallikerReference: InlineTextFilter<GridRow>['filterFn']
  manager: InlineTextFilter<GridRow>['filterFn']
  product: ModalContractsDropdownFilter<GridRow>['filterFn']
  nature: ModalContractsDropdownFilter<GridRow>['filterFn']
  location: ModalContractsDropdownFilter<GridRow>['filterFn']
  license: InlineTextFilter<GridRow>['filterFn']
  duration: InlineTextFilter<GridRow>['filterFn']
  distance: InlineTextFilter<GridRow>['filterFn']
  price: InlineTextFilter<GridRow>['filterFn']
  unit: ModalContractsDropdownFilter<GridRow>['filterFn']
  administration: ModalContractsDropdownFilter<GridRow>['filterFn']
  endIntention: ModalContractsDropdownFilter<GridRow>['filterFn']
  replacementState: ModalReplacementStateFilter<GridRow>['filterFn']
}
// #endregion
const year3000 = 32535122400000
export function GeneralContractsFunctional(props: RouteComponentProps<{ companyId?: string; daysAmount?: string }>) {
  const [selectedCustomerKeys, setSelectedCustomersKeys] = useState<string[]>()
  const [selectedDriver, setSelectedDriver] = useState<DriverFilter>()
  const [showWarning, setShowWarning] = useState(false)
  const user = useAppSelector((state) => state.appData.currentUser)
  const isInterfleetPro = useIsInterfleetPro()
  const customerIdFromUrl = props.match.params.companyId
  const contractExpirationFromUrl = props.match.params.daysAmount

  const [contractHistory, setContractHistory] = useState<string>()
  const [expiringDaysFilter, saveExpiringDaysFilter] = useSettings('expiringDaysFilter')
  const [contractExpiration, setContractExpiration] = useState<string>(contractExpirationFromUrl ?? expiringDaysFilter)

  // #region CUDSTOM HOOKS
  const { t } = useTranslation('common')
  const allUnits = useGetAllUnitsQuery()
  const allTenants = useGetTenantsQuery()
  const allOrdersForms = useGetOrdersFormsQuery()
  const contracts = useGetContractsBySecurityKeysQuery(
    { securityKeys: selectedCustomerKeys! },
    { skip: !selectedCustomerKeys?.length }
  )
  const { customers: filteredCustomers } = useFilteredCustomers({
    status: 'ACTIVE',
  })
  const {
    getReplacementCondition,
    getReplacementStateExportString,
    calculateReplacementSortValue,
    selectedReplacementFormat,
  } = useReplacementCondition()
  // #endregion

  // #region DATA MAPS
  const customerMap = useMemo(() => Arr.toHashMap(filteredCustomers ?? [], (c) => c.value), [filteredCustomers])
  const unitsMap = useMemo(() => Arr.toHashMap(allUnits.data ?? [], (c) => c.value), [allUnits.data])
  const contractsMaps = useMemo(() => Arr.toHashMap(contracts.data ?? [], (c) => c.id), [contracts.data])
  const replacedContractsMaps = useMemo(
    () => Arr.toHashMap(contracts.data ?? [], (c) => c.replacedContractId),
    [contracts.data]
  )
  // #endregion

  // #region USE CALLBACKS
  const getCostCenterName = useCallback(
    (contract: ContractViewModel) => {
      if (unitsMap && contract.owner) {
        const relatedUnit = unitsMap[contract.owner]
        return relatedUnit && relatedUnit.unit ? relatedUnit.unit.name : t('NO_COST_CENTER')
      }
      return ''
    },
    [t, unitsMap]
  )

  const getReplacedContracts = useCallback(
    (contract: ContractViewModel) => {
      const futureContract: ContractViewModel | undefined = replacedContractsMaps[contract.id]
      const replacedContract: ContractViewModel | undefined = contract.replacedContractId
        ? contractsMaps[contract.replacedContractId]
        : undefined

      return { futureContract, replacedContract }
    },
    [contractsMaps, replacedContractsMaps]
  )

  const getDriverName = useCallback(
    (row: GridRow) => {
      const c = row.contract
      const driverName = c.vehicle?.driver?.displayName
      const value =
        !!driverName && !c.poolCar
          ? driverName
          : !c.poolCar && !driverName
          ? t('NOT_ASSIGNED')
          : c.poolCar && !!c.location
          ? `${t('POOL_CAR')} | ${c.location}`
          : c.poolCar && (!c.location || !!driverName)
          ? `${t('POOL_CAR')}`
          : ''
      return value
    },
    [t]
  )

  const getCellClass = useCallback((row: GridRow) => {
    return `contractStatus contractStatus-${row.state?.toLowerCase()}`
  }, [])

  const updateExpirationDate = useCallback(
    (val: string) => {
      setContractExpiration(val)
      saveExpiringDaysFilter(val)
    },
    [saveExpiringDaysFilter]
  )
  // #endregion

  // #region FilterFNS
  const filterFns: GeneralContractsFilters = useMemo(() => {
    return {
      orderStatus: (item, value) => {
        const status = item.state
        if (status && value.includes(status)) return true
        return false
      },
      contract: (item, value) => !!item.contract.reference?.includes(value),
      costCenter: (item, value) => {
        const costCenter = getCostCenterName(item.contract)
        if (costCenter && value.includes(costCenter)) return true
        return false
      },
      replacedContractsRefNumber: (item, value) => {
        if (
          item.futureContract ||
          (item?.contract?.quoteReason === QuoteReason.Replacement && !!item?.contract.replacedContractId)
        ) {
          if (item.futureContract?.reference?.includes(value) || item.replacedContract?.reference?.includes(value))
            return true
        }
        return false
      },
      makes: (item, value) => !!item.contract.vehicle?.makeName && value.includes(item.contract.vehicle.makeName),
      model: (item, value) =>
        !!item.contract.vehicle?.modelName &&
        item.contract.vehicle.modelName.toLowerCase().includes(value.toLowerCase()),
      driver: (item, value) => {
        const c = item.contract
        const driverName = c.vehicle?.driver?.displayName
        const poolcar = 'Poolcar'
        const notAssigned = 'Not assigned'

        const result =
          !!driverName && !c.poolCar
            ? driverName
            : !c.poolCar && !driverName
            ? notAssigned
            : c.poolCar && !!c.location
            ? `${poolcar} | ${c.location}`
            : c.poolCar && (!c.location || !!driverName)
            ? poolcar
            : ''
        const matchesDriver = value.drivers && !c.poolCar && !!driverName
        const matchesPoolcar = value.pool_cars && result.includes(poolcar)
        const matchesNotAssigned = value.not_assigned && result.includes(notAssigned)

        const matchesSearchString =
          !!value.searchString && result.toLowerCase().includes(value.searchString.toLowerCase())

        if (value.drivers && matchesDriver) {
          return matchesSearchString || !value.searchString
        }

        if (value.pool_cars && matchesPoolcar) {
          return matchesSearchString || !value.searchString
        }

        if (value.not_assigned && matchesNotAssigned) {
          return matchesSearchString || !value.searchString
        }

        if (!value.drivers && !value.pool_cars && !value.not_assigned) {
          return matchesSearchString
        }

        return false
      },
      gallikerReference: (item, value) => {
        const galReference: string = item.contract.vehicle?.['GallikerReference']
        if (galReference && galReference.toLowerCase().includes(value.toLowerCase())) return true
        return false
      },
      manager: (item, value) => {
        if (item.manager && item.manager.toLowerCase().includes(value.toLowerCase())) return true
        return false
      },
      product: (item, value) => {
        const productId = item.contract.productId
        if (productId) {
          const product = productTypeIdMap.get(productId.toString())
          if (product && value.includes(product)) return true
        }
        return false
      },
      nature: (item, value) => !!item.contract.vehicle?.nature && value.includes(item.contract.vehicle?.nature),
      location: (item, value) => !!item.contract.location && value.includes(item.contract.location),
      license: (item, value) => {
        const licenseNumber = item.contract.vehicle?.licensePlate?.licenseNumber ?? ''
        const country = item.contract.vehicle?.licensePlate?.country ?? ''
        const string = country + licenseNumber
        return string.toLowerCase().includes(value.toLowerCase())
      },
      duration: (item, value) => !!item.contract.duration?.toString().includes(value),
      distance: (item, value) => !!item.contract.distance?.toString().includes(value),
      price: (item, value) => !!item.contract.leasePrice?.toFixed(2).includes(value),
      unit: (item, value) => {
        const units = item.unit
        if (units && value.includes(units)) return true
        return false
      },
      administration: (item, value) => {
        const administrations = item.administration
        if (administrations && value.includes(administrations)) return true
        return false
      },
      endIntention: (item, value) => {
        const endIntention = item.contract.endIntention
        if (endIntention && value.includes(endIntention)) return true
        return false
      },
      replacementState: (item, value) => {
        const { hasReplacement, missingReplacement, noData, returnWithoutReplacement, processingOrder } = value
        const hasFutureContract = !!item.futureContract
        const missingRep = !!item.replacementOrderCondition?.missingReplacementOrder
        const returnWithoutRep = !!item.replacementOrderCondition?.returnWithoutReplacement
        const processing = !!item.replacementOrderCondition?.processingOrder

        const allReplacementFiltersSelected =
          hasReplacement && missingReplacement && noData && returnWithoutReplacement && processingOrder
        if (allReplacementFiltersSelected) return true

        return (
          (noData && !hasFutureContract && !returnWithoutRep && !missingRep) ||
          (missingReplacement && missingRep) ||
          (hasReplacement && hasFutureContract && !returnWithoutRep && !missingRep) ||
          (returnWithoutReplacement && returnWithoutRep) ||
          (processingOrder && processing && !returnWithoutRep)
        )
      },
    }
  }, [getCostCenterName])
  // #endregion

  useEffect(() => {
    if (customerIdFromUrl) {
      if (customerIdFromUrl === 'allCustomers') {
        const allCustomersKeys = Object.values(customerMap).map((customer) => customer?.value ?? '')
        if (allCustomersKeys.length) setSelectedCustomersKeys(allCustomersKeys)
        return
      }
      const customer = customerMap[`$${customerIdFromUrl.toUpperCase()}`]
      if (customer) setSelectedCustomersKeys([customer.value!])
    }
  }, [customerIdFromUrl, customerMap])

  // #region DataRows
  const dataRows = useMemo(() => {
    const initializedContracts = contracts.currentData?.filter((c) => c.state !== ContractState.Initialized)
    const dataRows: GridRow[] =
      initializedContracts?.map((contract) => {
        const { replacedContract, futureContract } = getReplacedContracts(contract)
        const customerKey = contract.owner?.split('//')[0]
        const customer = customerKey ? customerMap[customerKey] : undefined
        const costCenter = getCostCenterName(contract)
        const unitAccount = unitsMap[contract.owner ?? '']
        let unit: string | undefined = undefined
        let administration: string | undefined = undefined
        if (unitAccount) {
          unit = unitAccount.unit?.unit
          administration = unitAccount.unit?.administration
        }
        const state = Contracts.getExtendedContractState(contract, Number(contractExpiration))

        const contractEndDate = new Date(contract.endDate)
        const gallikerReference = contract.vehicle?.['GallikerReference']
        const condition = getReplacementCondition({
          endIntention: contract.endIntention,
          endDate: contractEndDate.getTime() < year3000 ? contractEndDate : undefined,
          state,
          gallikerReference,
          futureContract,
          expiringContractsFilter: contractExpiration,
        })

        const manager = contract.managers?.[0]?.displayName
        return {
          contract,
          replacedContract,
          futureContract,
          customer,
          costCenter,
          unit,
          administration,
          state,
          replacementOrderCondition: condition,
          manager,
          type: 'ROW',
        }
      }) ?? []
    return dataRows
  }, [
    contracts.currentData,
    getReplacedContracts,
    customerMap,
    getCostCenterName,
    unitsMap,
    contractExpiration,
    getReplacementCondition,
  ])

  const selectorOptionsList = useMemo(() => {
    const uniqueStatuses = new Set<string>()
    const uniqueNatures = new Set<string>()
    const uniqueCostCenters = new Set<string>()
    const uniqueMakes = new Set<string>()
    const uniqueProducts = new Set<string>()
    const uniqueLocation = new Set<string>()
    const uniqueUnits = new Set<string>()
    const uniqueAdministrations = new Set<string>()
    const uniqueEndIntentions = new Set<string>()
    const options: SelectionState = {
      hasReplacement: false,
      missingReplacement: false,
      noData: false,
      returnWithoutReplacement: false,
      processingOrder: false,
    }
    dataRows.forEach((row) => {
      const costCenter = getCostCenterName(row.contract)
      if (costCenter) {
        uniqueCostCenters.add(costCenter)
      }
      if (row.state) {
        uniqueStatuses.add(row.state)
      }
      if (row.contract.vehicle?.nature) {
        uniqueNatures.add(row.contract.vehicle.nature)
      }
      if (row.contract.vehicle?.makeName) {
        uniqueMakes.add(row.contract.vehicle?.makeName)
      }
      if (row.contract.location) {
        uniqueLocation.add(row.contract.location)
      }
      if (row.contract.productId) {
        const product = productTypeIdMap.get(row.contract.productId.toString())
        if (product) {
          uniqueProducts.add(product)
        }
      }
      if (row.administration) {
        uniqueAdministrations.add(row.administration)
      }
      if (row.unit) {
        uniqueUnits.add(row.unit)
      }
      if (row.contract.endIntention) {
        uniqueEndIntentions.add(row.contract.endIntention)
      }

      const hasFutureContract = !!row.futureContract
      const missingRep = !!row.replacementOrderCondition?.missingReplacementOrder
      const returnWithoutRep = !!row.replacementOrderCondition?.returnWithoutReplacement
      const processing = !!row.replacementOrderCondition?.processingOrder

      if (!options.noData) options.noData = !hasFutureContract && !returnWithoutRep && !missingRep
      if (!options.missingReplacement) options.missingReplacement = missingRep
      if (!options.hasReplacement) options.hasReplacement = hasFutureContract && !returnWithoutRep && !missingRep
      if (!options.returnWithoutReplacement) options.returnWithoutReplacement = returnWithoutRep
      if (!options.processingOrder) options.processingOrder = processing && !returnWithoutRep
    })

    return {
      status: [...uniqueStatuses].sort(),
      nature: [...uniqueNatures].sort(),
      costCenters: [...uniqueCostCenters].sort(),
      makes: [...uniqueMakes].sort(),
      products: [...uniqueProducts].sort(),
      location: [...uniqueLocation].sort(),
      unit: [...uniqueUnits].sort(),
      administration: [...uniqueAdministrations].sort(),
      endIntention: [...uniqueEndIntentions].sort(),
      replacementState: options,
    }
  }, [dataRows, getCostCenterName])

  // #endregion

  // #region Columns
  const columns = useMemo((): GridColumn<GridRow>[] => {
    const disableClearBtn =
      (filteredCustomers?.length === 0 && selectedCustomerKeys?.length === 1) ||
      (filteredCustomers?.length === 1 && filteredCustomers[0].value === selectedCustomerKeys?.[0])

    const durations = ['30', '90', '180', '360', '450', '540', '720', '900', '1080', '1440']
    const getSelectorLabel = (incomingValue?: string) => {
      if (!incomingValue) return ''
      const val = Number(incomingValue)
      return val / 30 === 1 || val / 30 === 3 ? `${val} ${t('DAYS')}` : `${val / 30} ${t('MONTHS')}`
    }
    function displayBasicValue(value: string, customTitle?: string) {
      return <span title={customTitle || value}>{value}</span>
    }
    const cols: GridColumn<GridRow>[] = [
      {
        key: ColKeysEnum.company,
        name: t('COMPANY'),
        width: 320,
        resizable: false,
        filter: {
          type: 'modal_delivery_customer_filter',
          data: filteredCustomers,
          format: (value) => value?.map((c) => customerMap[c]?.customer?.name ?? ''),
          onChange: (value) => {
            const validKeys = value?.filter((c) => customerMap[c])
            setSelectedCustomersKeys(validKeys?.length ? validKeys : undefined)
          },
          value: selectedCustomerKeys,
          readOnly: disableClearBtn,
          cascadeClear: ColKeysEnum.costCenter,
          autoTrigger: !customerIdFromUrl,
          ignoreSaved: !!customerIdFromUrl,
        },
        renderCell: ({ row }) => displayBasicValue(row.customer?.customer?.name ?? ''),
        cellClass: getCellClass,
        exportFormat: (row) => row.customer?.customer?.name,
        sortBy: (row) => row.customer?.customer?.name,
      },
      {
        key: ColKeysEnum.costCenter,
        name: t('COST_CENTER'),
        width: 190,
        resizable: false,
        filter: {
          type: 'modal_contracts_dropdown_filter',
          title: t('COST_CENTER_SEARCH'),
          filterFn: filterFns.costCenter,
          data: selectorOptionsList.costCenters,
        },
        renderCell: ({ row }) => (
          <ContractCostCenterCell
            contract={row.contract}
            units={unitsMap}
            onHistoryToggle={(id) => setContractHistory(id)}
            showHistory={contractHistory === row.contract.id}
          />
        ),
        cellClass: getCellClass,
        exportFormat: (row) => getCostCenterName(row.contract),
        sortBy: (row) => getCostCenterName(row.contract),
      },
      {
        key: ColKeysEnum.nature,
        name: t('OBJ_TYPE'),
        width: 90,
        resizable: false,
        filter: {
          type: 'modal_contracts_dropdown_filter',
          title: t('OBJ_TYPE'),
          filterFn: filterFns.nature,
          data: selectorOptionsList.nature,
          format: (items) => items?.map((v) => t(`${v.toUpperCase()}`)),
        },
        renderCell({ row }) {
          if (row.contract.vehicle?.nature) {
            return <ObjTypeCell nature={row.contract.vehicle?.nature} />
          }
        },
        cellClass: getCellClass,
        exportFormat: (row) => row.contract.vehicle?.nature,
        sortBy: (row) => row.contract.vehicle?.nature,
      },
      {
        key: ColKeysEnum.contract,
        name: t('CONTRACT'),
        width: 140,
        resizable: false,
        filter: {
          type: 'inline_text_filter',
          filterFn: filterFns.contract,
        },
        renderCell({ row }) {
          if (row.contract.id) {
            return (
              <ContractCell
                user={user}
                allOrdersForms={allOrdersForms.data}
                allTenants={allTenants.data}
                filteredCustomers={filteredCustomers}
                row={row}
              />
            )
          }
        },
        cellClass: getCellClass,
        exportFormat: (row) => row.contract.reference,
        sortBy: (row) => row.contract.reference,
      },
      {
        key: ColKeysEnum.replacedContractsRefNumber,
        name: (
          <i
            title={t('REPLACED_CONTRACT')}
            className={'icon-change-car'}
            style={{ fontSize: '18px', verticalAlign: 'sub', margin: '0px 5px' }}
          />
        ),
        label: t('REPLACED_CONTRACT'),
        filter: {
          type: 'modal_replacement_state_filter',
          data: selectorOptionsList.replacementState,
          filterFn: filterFns.replacementState,
          format: selectedReplacementFormat,
        },
        width: 60,
        resizable: false,
        renderCell: ({ row }) => <ReplacementStateCell row={row} />,
        cellClass: getCellClass,
        exportFormat: getReplacementStateExportString,
        sortBy: calculateReplacementSortValue,
      },
      {
        key: ColKeysEnum.location,
        name: t('LOCATION'),
        minWidth: 100,
        headerCellClass: 'general-contracts-header',
        remove: !isInterfleetPro && !user?.isInternalUser,
        filter: {
          type: 'modal_contracts_dropdown_filter',
          title: t('LOCATION'),
          filterFn: filterFns.location,
          data: selectorOptionsList.location,
        },
        renderCell: ({ row }) => row.contract.location ?? '',
        cellClass: getCellClass,
        exportFormat: (row) => row.contract.location ?? '',
        sortBy: (row) => row.contract.location ?? '',
      },
      {
        key: ColKeysEnum.driverName,
        name: t('DRIVER_NAME_OR_POOL_CAR'),
        width: 150,
        minWidth: 150,
        resizable: false,
        filter: {
          type: 'modal_driver_filter',
          filterFn: filterFns.driver,
          format: (value) => {
            if (!value) {
              return
            }
            const result = []
            if (value.pool_cars) result.push(t('POOL_CARS'))
            if (value.not_assigned) result.push(t('NOT_ASSIGNED'))
            if (value.drivers) result.push(t('DRIVERS'))
            if (value.searchString) result.push(value.searchString)
            return result
          },
          value: selectedDriver,
          onChange: (value) => setSelectedDriver(value),
        },
        renderCell: ({ row }) => displayBasicValue(getDriverName(row)),
        cellClass: getCellClass,
        exportFormat: getDriverName,
        sortBy: getDriverName,
      },
      {
        key: ColKeysEnum.gallikerReference,
        name: t('LOGISTICID'),
        width: 100,
        minWidth: 150,
        resizable: false,
        remove: !isInterfleetPro && !user?.isInternalUser,
        filter: {
          type: 'inline_text_filter',
          filterFn: filterFns.gallikerReference,
        },
        renderCell: ({ row }) => displayBasicValue(row.contract.vehicle?.['GallikerReference']),
        cellClass: getCellClass,
        exportFormat: (row) => row.contract.vehicle?.['GallikerReference'],
        sortBy: (row) => row.contract.vehicle?.['GallikerReference'],
      },
      {
        key: ColKeysEnum.chassisNumber,
        name: t('VIN_NUMBER'),
        minWidth: 150,
        hide: true,
        renderCell: ({ row }) => displayBasicValue(row.contract.vehicle?.chassisNumber ?? ''),
        cellClass: getCellClass,
        exportFormat: (row) => row.contract.vehicle?.chassisNumber ?? '',
        sortBy: (row) => row.contract.vehicle?.chassisNumber ?? '',
      },
      {
        key: ColKeysEnum.motorNumber,
        name: t('MOTOR_NUMBER'),
        minWidth: 150,
        hide: true,
        renderCell: ({ row }) => displayBasicValue(row.contract.vehicle?.motorNumber ?? ''),
        cellClass: getCellClass,
        exportFormat: (row) => row.contract.vehicle?.motorNumber ?? '',
        sortBy: (row) => row.contract.vehicle?.motorNumber ?? '',
      },
      {
        key: ColKeysEnum.objectManager,
        name: t('OBJECT_MANAGER'),
        minWidth: 175,
        remove: !isInterfleetPro && !user?.isInternalUser,
        filter: {
          type: 'inline_text_filter',
          filterFn: filterFns.manager,
        },
        hide: true,
        headerCellClass: 'general-contracts-header',
        renderCell: ({ row }) => displayBasicValue(row.manager ?? ''),
        sortBy: (row) => row.manager ?? '',
        cellClass: getCellClass,
      },
      {
        key: ColKeysEnum.vehicle,
        width: 80,
        name: t('MAKES'),
        label: t('MAKES_AND_MODEL'),
        resizable: false,
        filter: {
          type: 'modal_contracts_dropdown_filter',
          title: t('MAKES'),
          filterFn: filterFns.makes,
          data: selectorOptionsList.makes,
        },
        renderCell({ row }) {
          const vehicle = row.contract.vehicle
          if (vehicle?.makeName && vehicle?.makeName.length >= 2) {
            const logos =
              vehicle?.makeName.toLowerCase().indexOf('ladestation') > -1
                ? 'dummy'
                : vehicle?.makeName.replace(/\s+/g, '-').toLowerCase()
            return (
              <div className="vehicleDescription">
                {logos && (
                  <img
                    src={`../../../../BW/${logos.normalize('NFD').replace(/[\u0300-\u036f]/g, '')}.svg`}
                    width="23"
                    height="23"
                  />
                )}
                {vehicle.description && displayBasicValue(vehicle.description)}
              </div>
            )
          }
        },
        cellClass: getCellClass,
        exportFormat: (row) => row.contract.vehicle?.description,
        sortBy: (row) => row.contract.vehicle?.makeName,
      },
      {
        key: ColKeysEnum.model,
        name: t('MODEL'),
        minWidth: 180,
        resizable: false,
        parentKey: ColKeysEnum.vehicle,
        filter: { type: 'inline_text_filter', filterFn: filterFns.model },
        sortBy: (row) => row.contract.vehicle?.description,
      },
      {
        key: ColKeysEnum.licenceNumber,
        name: t('LICENSE_NUMBER'),
        width: 120,
        headerCellClass: 'general-contracts-header',
        filter: { type: 'inline_text_filter', filterFn: filterFns.license },
        renderCell({ row }) {
          const licenseNumber = row.contract.vehicle?.licensePlate?.licenseNumber
          const country = row.contract.vehicle?.licensePlate?.country
          if (licenseNumber && licenseNumber?.length > 2) {
            return <LicencePlateFlag countryCode={country} licenceNumber={licenseNumber} width="12" height="14" />
          }
        },
        exportFormat: (row) => {
          const licenseNumber = row.contract.vehicle?.licensePlate?.licenseNumber
          if (licenseNumber) return licenseNumber
        },
        sortBy: (row) => row.contract.vehicle?.licensePlate?.country,
        cellClass: getCellClass,
      },
      {
        key: ColKeysEnum.distance,
        name: t('CONTRACT_DISTANCE'),
        width: 90,
        headerCellClass: 'general-contracts-header',
        filter: { type: 'inline_text_filter', filterFn: filterFns.distance },
        renderCell({ row }) {
          const distance = row.contract.distance
          if (distance) {
            const value = formatNumberToLocale(distance, 0)
            const tooltip = `${value} km`
            return (
              <span className="right block">
                <span title={tooltip}>{value}</span>
              </span>
            )
          }
        },
        exportFormat: (row) => row.contract.distance?.toString(),
        sortBy: (row) => row.contract.distance,
        cellClass: getCellClass,
      },
      {
        key: ColKeysEnum.duration,
        name: t('CONTRACT_DURATION'),
        width: 80,
        headerCellClass: 'general-contracts-header',
        filter: { type: 'inline_text_filter', filterFn: filterFns.duration },
        renderCell({ row }) {
          if (row.contract.duration) {
            return <span className="center block">{row.contract.duration}</span>
          }
        },
        exportFormat: (row) => row.contract.duration?.toString(),
        sortBy: (row) => row.contract.duration,
        cellClass: getCellClass,
      },
      {
        key: ColKeysEnum.price,
        name: t('CONTRACT_PRICE'),
        width: 90,
        remove: user?.isGuest,
        headerCellClass: 'general-contracts-header',
        filter: { type: 'inline_text_filter', filterFn: filterFns.price },
        renderCell({ row }) {
          const price = Number(row.contract.leasePrice?.toFixed(2))
          if (price) {
            const value = formatCurrencyToSwissLocale(price)
            const tooltip = `${value} ${t('CHF')}`
            return (
              <span className="right block">
                <span title={tooltip}>{value}</span>
              </span>
            )
          }
        },
        exportFormat: (row) => row.contract.leasePrice?.toFixed(2),
        sortBy: (row) => row.contract.leasePrice,
        cellClass: getCellClass,
      },

      {
        key: ColKeysEnum.product,
        name: t('PRODUCT'),
        width: 100,
        resizable: false,
        filter: {
          type: 'modal_contracts_dropdown_filter',
          title: t('PRODUCT_SEARCH'),
          transPrefix: 'PRODUCT_SHORT_',
          filterFn: filterFns.product,
          data: selectorOptionsList.products ?? [],
        },
        renderCell({ row }) {
          const productId = row.contract.productId
          if (productId) {
            const product = productTypeIdMap.get(productId.toString())
            if (product) {
              return displayBasicValue(
                t(`PRODUCT_SHORT_${product}`.toUpperCase()),
                t(`PRODUCT_FULL_${product}`.toUpperCase())
              )
            }
          }
        },
        cellClass: getCellClass,
        exportFormat(row) {
          if (row.contract.productId) {
            const product = productTypeIdMap.get(row.contract.productId.toString())
            if (product) {
              return t(`PRODUCT_SHORT_${product}`.toUpperCase())
            }
          }
        },
        sortBy: (row) => {
          if (row.contract.productId) {
            const product = productTypeIdMap.get(row.contract.productId.toString())
            if (product) {
              return t(`PRODUCT_FULL_${product}`.toUpperCase())
            }
          }
        },
      },
      {
        key: ColKeysEnum.endIntention,
        name: t('END_INTENTION'),
        width: 150,
        resizable: false,
        remove: !user?.isInternalUser,
        filter: {
          type: 'modal_contracts_dropdown_filter',
          data: selectorOptionsList.endIntention,
          filterFn: filterFns.endIntention,
          title: t('END_INTENTION'),
        },
        renderCell: ({ row }) => t(`${row.contract.endIntention}`.toUpperCase()),
        cellClass: getCellClass,
        exportFormat: (row) => t(`${row.contract.endIntention}`.toUpperCase()),
        sortBy: (row) => t(`${row.contract.endIntention}`.toUpperCase()),
      },
      {
        key: ColKeysEnum.startDate,
        name: t('START_DATE'),
        width: 100,
        resizable: false,
        renderCell: ({ row }) => printDate(row.contract.startDate),
        cellClass: getCellClass,
        exportFormat: (row) => (isDateValid(row.contract.startDate) ? printDate(row.contract.startDate) : undefined),
        sortBy: (row) => (isDateValid(row.contract.startDate) ? new Date(row.contract.startDate) : undefined),
      },
      {
        key: ColKeysEnum.endDate,
        name: t('END_DATE'),
        width: 100,
        resizable: false,
        filter: {
          type: 'inline_selector_filter',
          data: durations,
          format: getSelectorLabel,
          position: 'header-right',
          onChange: (val) => val && updateExpirationDate(val),
          value: contractExpiration,
          ignoreSaved: true,
        },
        renderCell: ({ row }) => printDate(row.contract.endDate),
        cellClass: getCellClass,
        exportFormat: (row) => (isDateValid(row.contract.endDate) ? printDate(row.contract.endDate) : undefined),
        sortBy: (row) => (isDateValid(row.contract.endDate) ? new Date(row.contract.endDate) : undefined),
      },
      {
        key: ColKeysEnum.state,
        name: t('STATUS'),
        width: 150,
        resizable: false,
        filter: {
          type: 'modal_contracts_dropdown_filter',
          title: t('STATUS_SEARCH'),
          filterFn: filterFns.orderStatus,
          data: selectorOptionsList.status,
          format: (items) => items?.map((v) => t(`${v.toUpperCase()}`)),
        },
        renderCell({ row }) {
          if (row.state)
            return (
              <label
                className={`contractListStatus statusArea ${row.state.toLowerCase()}`}
                title={t(`CONTRACT_${row.state}`.toUpperCase())}
              >
                {t(`CONTRACT_${row.state}`.toUpperCase())}
              </label>
            )
        },
        cellClass: getCellClass,
        exportFormat: (row) => row.state,
        sortBy: (row) => row.state,
      },
      {
        key: ColKeysEnum.Administration,
        name: t('ADMINISTRATION'),
        width: 100,
        resizable: false,
        remove: !user?.isInternalUser,
        filter: {
          type: 'modal_contracts_dropdown_filter',
          title: t('ADMINISTRATION'),
          data: selectorOptionsList.administration,
          filterFn: filterFns.administration,
        },
        renderCell: ({ row }) => row.administration,
        cellClass: getCellClass,
        exportFormat: (row) => row.administration,
        sortBy: (row) => row.administration,
      },
      {
        key: ColKeysEnum.Unit,
        name: t('UNIT'),
        width: 100,
        resizable: false,
        remove: !user?.isInternalUser,
        filter: {
          type: 'modal_contracts_dropdown_filter',
          title: t('UNIT'),
          data: selectorOptionsList.unit,
          filterFn: filterFns.unit,
        },
        renderCell: ({ row }) => row.unit,
        cellClass: getCellClass,
        exportFormat: (row) => row.unit,
        sortBy: (row) => row.unit,
      },
    ]
    return cols
  }, [
    filteredCustomers,
    selectedCustomerKeys,
    t,
    customerIdFromUrl,
    getCellClass,
    filterFns,
    selectorOptionsList,
    selectedDriver,
    getDriverName,
    customerMap,
    unitsMap,
    contractHistory,
    getCostCenterName,
    updateExpirationDate,
    allTenants.data,
    allOrdersForms.data,
    user,
    isInterfleetPro,
    calculateReplacementSortValue,
    getReplacementStateExportString,
    selectedReplacementFormat,
    contractExpiration,
  ])
  // #endregion

  // #region JSX
  return (
    <Loader className="grid-size" blocking={contracts.isFetching || !filteredCustomers}>
      <Grid
        id="GeneralContracts"
        columns={columns}
        rows={dataRows}
        rowKeyGetter={(item) => {
          return item.contract.id
        }}
        pagination={true}
        showTotalItems={true}
        onReload={
          selectedCustomerKeys?.length
            ? () => {
                contracts.refetch()
              }
            : undefined
        }
        customClass="generalContracts"
        exportTitle="GeneralContracts"
      />

      <Alert
        visible={showWarning}
        onClose={() => {
          setShowWarning(false)
        }}
        title={t('WARNING')}
        message={`${t('REQUEST_IS_NOT_AVAILABLE_ANYMORE')}`}
      />
    </Loader>
  )
}
